import groupBy from "lodash/groupBy";
import { usePathname, useSearchParams } from "next/navigation";
import { ReactNode } from "react-markdown";

import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import FilterChipLink from "@/components/FilterChipLink";

import {
  AvailableFilter,
  FilterType,
  GroupFilter,
  ToggleFilterType,
} from "../../DynamicFilters/types";

const getFilterLabelOrChip = (
  filter: AvailableFilter,
  appliedValue: string,
) => {
  if (filter.type === FilterType.toggle) {
    const toggleFilter = filter as ToggleFilterType;
    return toggleFilter.label;
  } else {
    // * filter is some kind of group filter
    const groupFilter = filter as GroupFilter;
    return getGroupFilterValueChip(groupFilter, appliedValue);
  }
};

const getGroupFilterValueChip = (
  groupFilter: GroupFilter,
  appliedValue: string,
) => {
  const filterValuesMap = groupBy(groupFilter.values, "value");
  // * Sometimes we can't find the particular value for a filter, especially if its a filter with over 1000 choices (our aggregation bucket max for ES)
  return filterValuesMap[appliedValue]?.[0].chip || appliedValue;
};

const AppliedChips: React.FC<{
  availableFilters: AvailableFilter[];
  filterQueryStringKey?: (qsKey: string) => string;
}> = ({ availableFilters, filterQueryStringKey = (qsKey) => qsKey }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const targetURLSearchParams = new URLSearchParams(searchParams?.toString());
  // * Remove pagination params when clicking fulfillment filter link.
  targetURLSearchParams.delete("page");

  return availableFilters.reduce<ReactNode[]>((chips, filter) => {
    // * Exit early if search params are completely empty / unavailable
    if (!searchParams) {
      return chips;
    }

    // * Check for individual value
    const appliedFilterValue = searchParams.get(
      filterQueryStringKey(filter.name),
    );
    // * Check for bracketed collection
    const appliedFilterValues = searchParams.getAll(
      `${filterQueryStringKey(filter.name)}[]`,
    );

    if (appliedFilterValues.length > 0) {
      // * Filter is some kind of GroupFilter (checkbox or tile) since there are many values applied
      const groupFilter = filter as GroupFilter;
      return [
        ...chips,
        ...appliedFilterValues.map((appliedValue) => (
          <FilterChipLink
            key={`${filter.name}_${appliedValue}`}
            href={{
              pathname,
              query: toggleURLSearchParamValue(
                targetURLSearchParams,
                filterQueryStringKey(groupFilter.name),
                appliedValue,
              ).toString(),
            }}
          >
            {getGroupFilterValueChip(groupFilter, appliedValue)}
          </FilterChipLink>
        )),
      ];
    } else if (appliedFilterValue) {
      // * Should handle both individual GroupFilter values as well as ToggleFilter types
      return [
        ...chips,
        <FilterChipLink
          key={`${filter.name}_${appliedFilterValue}`}
          href={{
            pathname,
            query: toggleURLSearchParamValue(
              targetURLSearchParams,
              filterQueryStringKey(filter.name),
              appliedFilterValue,
            ).toString(),
          }}
        >
          {getFilterLabelOrChip(filter, appliedFilterValue)}
        </FilterChipLink>,
      ];
    } else {
      // * Exit if we can't find any applied values for this filter category
      return chips;
    }
  }, []);
};

export default AppliedChips;
